import React, { useCallback, useMemo } from 'react';

import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import type { SalesChannel } from '@flipdish/orgmanagement';
import CircularProgress from '@mui/material/CircularProgress';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from 'components/RMS/utils/permissions';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import PermissionsComponent from 'react-redux-permissions';
import { useHistory } from 'react-router-dom';

import EmptyComponent from '@fd/ui/EmptyComponent';
import ErrorComponent from '@fd/ui/ErrorComponent';
import PageLayout from '@fd/ui/Layout';
import LazyComponent from '@fd/ui/LazyComponent';
import Tabs from '@fd/ui/Tabs';

import { entitlementConstants } from '../../../constants/entitlement.constants';
import useHasEntitlement from '../../../custom-hooks/useHasEntitlement';
import { lazyWithRetry } from '../../../helpers/utilities';
import { flagService } from '../../../services/flagService/flagService';

const SalesChannelList = lazyWithRetry(() => import('../../RMS/SalesChannels/SalesChannelList'));

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

type MappedState = ReturnType<typeof mapStateToProps>;

const PhoneAgentSettings = (props: MappedState) => {
  const { translate, currentApp, selectedProperty, showSalesChannelsFlag, orgId } = props;
  const classes = useStyles();
  const history = useHistory();
  const {
    hasEntitlement: hasPhoneAgentEntitlement,
    isLoading: isLoadingEntitlement,
    isError: isEntitlementError,
  } = useHasEntitlement(orgId, entitlementConstants.PhoneAgent.AiAgent);

  const handleChannelClick = useCallback(
    (salesChannel: SalesChannel) => {
      if (!currentApp?.AppId || !selectedProperty?.code || !salesChannel.salesChannelId) return;

      history.push(
        `/${currentApp.AppId}/sales-channels/phone-agent/property/${selectedProperty.code}/sales-channel/${salesChannel.salesChannelId}`
      );
    },
    [currentApp?.AppId, history, selectedProperty?.code]
  );

  const appId = currentApp?.AppId;
  const {
    data,
    isLoading: isLoadingAssociations,
    error: associationsError,
  } = useQueryRMSCommonDataHook(appId ?? '', { enabled: !!appId });
  const showSalesChannels = data.associations?.isMigratedToSalesChannels || showSalesChannelsFlag;

  const tabItems = useMemo(
    () =>
      showSalesChannels
        ? [
            {
              label: translate('Sales_Channels') as string,
              component: (
                <LazyComponent>
                  <SalesChannelList
                    filterByType={[SalesChannelType.FLIPDISH_PHONE_AGENT]}
                    onChannelClick={handleChannelClick}
                    hideFilter={false}
                  />
                </LazyComponent>
              ),
              queryString: 'sales-channels',
            },
          ]
        : [],
    [showSalesChannels, translate, handleChannelClick]
  );

  const renderContent = () => {
    if (isLoadingEntitlement) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    if (isEntitlementError) {
      return <ErrorComponent title="Phone_agent_load_error" />;
    }

    if (!hasPhoneAgentEntitlement) {
      return <EmptyComponent title="Phone_agent_not_available" noLink />;
    }

    if (!appId || isLoadingAssociations) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    if (associationsError) {
      return <ErrorComponent title="Something_went_wrong" />;
    }

    return tabItems.length > 0 ? <Tabs tabItems={tabItems} /> : null;
  };

  return (
    <PageLayout documentTitle="Phone_agent" title={<Translate id="Phone_agent" />}>
      <PermissionsComponent
        allowed={[
          'Owner',
          'ManagedOwner',
          'FlipdishStaff',
          ...propertyManagerRolePermission,
          ...propertyOwnerRolePermission,
        ]}
      >
        <div className={classes.container}>{renderContent()}</div>
      </PermissionsComponent>
    </PageLayout>
  );
};

const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
  selectedProperty: state.rms.selectedProperty,
  showSalesChannelsFlag: flagService.isFlagOn(state, 'showSalesChannelStructure'),
  currentApp: state.currentApp,
  orgId: state.rms.currentOrg?.orgId,
});

export default connect(mapStateToProps)(PhoneAgentSettings);
